<template>
  <v-form ref="form" @submit.prevent="submit" v-model="formValid">
    <v-row class="pb-0">
      <v-col cols="5">
        <p class="font-weight-bold black--text quotation-title mb-7">
          Importar KML ou preencher coordenadas
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        md="7"
        class="pr-sm-7 mt-2"
        v-if="!this.disabled && !this.hasKmlFiles"
      >
        <DropFile
          v-if="!this.disabled"
          @selected-files="addKmlFiles"
          v-on="$listeners"
          height="90px"
          width="600px"
          class="mb-5"
          format-files-acceptable=".kml,.kmz"
          :multiple="false"
        />
      </v-col>
      <v-col md="7" v-if="this.hasKmlFiles">
        <DocumentDataTable
          class="mb-5"
          :documents="getLoadedKmlFiles"
          @remove-file="onRemoveKmlFiles"
          :viewButton="false"
          :disabled="this.disabled"
        />
      </v-col>
    </v-row>
    <v-row class="pb-12">
      <GoogleMapsAPI
        ref="googleMapsAPI"
        :areaName="areaItem.nome"
        :coordenadas="areaItem.coordenadas"
        :expectedMinAreaSize="expectedMinAreaSize"
        :expectedMaxAreaSize="expectedMaxAreaSize"
        :readyOnly="!this.disabled"
      />
    </v-row>
    <v-dialog
      v-if="showModalKml"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="showModalKml"
      width="700px"
    >
      <v-row class="white px-6 py-4">
        <h3 class="black--text mb-4">Formato de arquivo inexperado</h3>
      </v-row>
      <v-row class="white px-6">
        <v-col cols="12" class="pr-0">
          <p style="text-align:justify">
            É necessário que o arquivo KML possua apenas uma área desenhada.
          </p>
        </v-col>
      </v-row>
      <v-row cols="12" class="white">
        <v-spacer />
        <v-col cols="3" class="mb-3 mr-3">
          <BaseButton
            class="white--text secondary"
            style="width:100%"
            @click="setShowModalKml(false)"
          >
            Ok
          </BaseButton>
        </v-col>
      </v-row>
    </v-dialog>
  </v-form>
</template>
<script>
import { requiredInput } from "@/utils/rules.js";
import { publicAxios } from "@/api/public.js";
import { mapActions, mapState } from "vuex";
import DropFile from "@/components/DropFile.vue";
import { FILE } from "@/mixins/DocumentsMixin";
import DocumentDataTable from "@/components/DocumentDataTable";
import GoogleMapsAPI from "@/components/GoogleMapsAPI";
import { QuotationMixin } from "@/mixins/NewQuotationMixin";

export default {
  data: () => ({
    axios: publicAxios, // responsavel por ler o kml como xml
    formValid: true,
    loading: false,
    requiredInput,
    timeout: null,
    map: null,
    google: null,
    loader: null,
    showModalKml: false
  }),
  mixins: [QuotationMixin],
  components: {
    DocumentDataTable,
    DropFile,
    GoogleMapsAPI
  },
  computed: {
    ...mapState(["loadingStatus"]),
    hasKmlFiles() {
      return this.areaItem.kmlFiles && this.areaItem.kmlFiles.length > 0;
    },
    fileGoogleAPI() {
      // se for arquivo kmz, o kml que foi descompactado estará na posicao 1, na 0 fica o kmz.
      if (this.hasKmlFiles && this.getFileFormat == "kml") {
        return this.areaItem.kmlFiles[0].url;
      } else if (this.hasKmlFiles && this.getFileFormat == "kmz") {
        return this.areaItem.kmlFiles[1].url;
      }

      return null;
    },
    getLoadedKmlFiles() {
      if (this.areaItem.kmlFiles) {
        return [this.areaItem.kmlFiles[0]]; //retorna a primeira posição que é o arquivo do upload, os demais sao os descompactados
      }
      return [];
    },
    getFileFormat() {
      if (this.hasKmlFiles) {
        if (this.getLoadedKmlFiles[0].arquivo) {
          return this.areaItem.kmlFiles[0].arquivo.url.substr(
            this.areaItem.kmlFiles[0].arquivo.url.length - 3
          );
        } else {
          return this.areaItem.kmlFiles[0].url.substr(
            this.areaItem.kmlFiles[0].url.length - 3
          );
        }
      }
      return "";
    }
  },
  methods: {
    ...mapActions(["hasLoading", "showAlertNotification"]),
    setShowModalKml(status) {
      this.showModalKml = status;
    },
    focusForm() {
      setTimeout(() => {
        window.scrollBy(0, 550);
      }, 200);
    },
    updateKmlFiles(item) {
      this.areaItem.kmlFiles = item;
    },
    async addKmlFiles(files) {
      const uniqueFileNames = new Set(
        this.areaItem.kmlFiles.map(kml => kml.arquivo.nome)
      );

      const newKmlFiles = files
        .filter(file => !uniqueFileNames.has(file.name))
        .map(file => ({
          arquivo: {
            [FILE]: file,
            nome: file.name
          }
        }));
      this.areaItem.kmlFiles =
        newKmlFiles[0].arquivo.nome.substr(
          newKmlFiles[0].arquivo.nome.length - 3
        ) == "kmz"
          ? (await this.submitZipFiles(newKmlFiles))[0]
          : await this.submitFiles(newKmlFiles);
      this.getCoordinatesFromKmlFile();
    },
    async getCoordinatesFromKmlFile() {
      await this.axios.get(this.fileGoogleAPI).then(response => {
        let parser = new DOMParser();
        let docXml = parser.parseFromString(response.data, "text/xml");

        // pega todos os poligonos do arquivo importado
        let polygonTag = docXml.getElementsByTagName("Polygon");
        if (polygonTag?.length > 1) {
          this.areaItem.kmlFiles = [];
          this.setShowModalKml(true);
          return;
        }
        // varre os poligonos do arquivo para carregar as posições
        polygonTag.forEach(polygon => {
          // cada poligono possui um tag xml coordinates com todas as posições marcadas
          let coordinateTag = polygon
            .getElementsByTagName("coordinates")[0]
            .innerHTML.split(" ");

          // trata os dados das coordenadas para conseguir importar
          let coordinates = [];
          coordinateTag.forEach(el => {
            let marker = el.split(",");
            // Caso venha lat e lng fora do padrão, ignora a coordenada
            if (!isNaN(marker[0]) && !isNaN(marker[1])) {
              coordinates.push({
                lat: Number(marker[1]),
                lng: Number(marker[0])
              });
            }
          });
          // No futuro, adicionar o código referente a criação de áreas automaticas aqui e setar a nova área com o coordinates.
          this.areaItem.coordenadas = coordinates;
        });
      });
    },
    onRemoveKmlFiles(item) {
      this.areaItem.kmlFiles = this.areaItem.kmlFiles.filter(document =>
        document.arquivo
          ? document.arquivo.nome !== item.arquivo.nome
          : document.nome !== item.nome
      );
      this.areaItem.coordenadas = [];
    },
    getCoordinates() {
      return this.$refs.googleMapsAPI.getCoordinates();
    },
    getAreaSizeInHa() {
      return this.$refs.googleMapsAPI.getAreaSizeInHa() ?? 0;
    }
  },
  props: {
    areaItem: {
      type: Object
    },
    disabled: {
      type: Boolean,
      default: false
    },
    alwaysOn: {
      type: Boolean,
      default: false
    },
    expectedMaxAreaSize: {
      type: Number,
      default: 0
    },
    expectedMinAreaSize: {
      type: Number,
      default: 0
    }
  }
};
</script>
